/**
 * Lecture des tokens de design depuis JavaScript.
 *
 * Canvas ne résout pas les variables CSS : Chart.js et D3 reçoivent des chaînes
 * qu'ils passent tels quels au contexte de dessin. Passer `var(--crit)` à un
 * `backgroundColor` produit une couleur invalide, donc un graphique vide ou noir.
 *
 * Ces helpers résolvent le token en sa valeur réelle au moment du rendu, ce qui
 * garde une seule source de vérité pour les couleurs sans casser le canvas.
 */

/** Valeurs de repli si le token n'est pas résolu (rendu hors document, tests). */
const FALLBACKS: Record<string, string> = {
  '--crit': '#B91C1C',
  '--warn': '#B45309',
  '--ok-fg': '#0F766E',
  '--info': '#3F4E63',
  '--accent': '#1e3a5f',
  '--text': '#0F172A',
  '--text-muted': '#475569',
  '--text-hint': '#94A3B8',
  '--border': '#E2E8F0',
  '--bg-card': '#ffffff',
  '--bg-surface': '#F1F5F9',
}

/**
 * Résout un token CSS en sa valeur calculée.
 * Accepte `--crit` comme `var(--crit)`, pour pouvoir passer directement une
 * valeur venant d'une table de correspondance.
 */
export function token(name: string): string {
  const clean = name.trim().replace(/^var\(\s*/, '').replace(/\s*\)$/, '')
  if (!clean.startsWith('--')) return name // déjà une couleur littérale

  if (typeof window !== 'undefined' && document?.documentElement) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(clean).trim()
    if (value) return value
  }
  return FALLBACKS[clean] ?? name
}

/** Résout un lot de tokens en une passe. */
export function tokens<T extends Record<string, string>>(map: T): Record<keyof T, string> {
  const out = {} as Record<keyof T, string>
  for (const key of Object.keys(map) as Array<keyof T>) {
    out[key] = token(map[key])
  }
  return out
}
